// useRuleActionUpdater.js

import { useCallback } from 'react';

/**
 * useRuleActionUpdater
 *
 * Provides helper callbacks for updating or replacing a single action row by key.
 *
 * @param {string} ruleKey - Parent rule key.
 * @param {function} updateRule - Rule updater callback.
 *
 * @returns {{updateAction: function, replaceAction: function}}
 */
const useRuleActionUpdater = ({ ruleKey, updateRule }) => {
  const updateAction = useCallback((actionKey, updater) => {
    updateRule(ruleKey, (prev) => ({
      ...prev,
      actions: (Array.isArray(prev.actions) ? prev.actions : []).map((entry) => {
        if (entry.key !== actionKey) return entry;
        return updater(entry, prev);
      }),
    }));
  }, [ruleKey, updateRule]);

  const replaceAction = useCallback((actionKey, nextActionBuilder) => {
    updateAction(actionKey, (entry, prevRule) => {
      const nextAction = nextActionBuilder(entry, prevRule) || entry;
      return {
        ...nextAction,
        key: entry.key,
      };
    });
  }, [updateAction]);

  return {
    updateAction,
    replaceAction,
  };
};

export default useRuleActionUpdater;
